import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  Button,
  TextField,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Modal,
  CircularProgress,
  IconButton,
  InputAdornment,
  useTheme
} from '@mui/material';
import { FaSearch, FaTrash } from 'react-icons/fa';
import { Pencil } from 'lucide-react';
import { useColorMode } from '../../../../../theme/ThemeContext';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import axios from 'axios';
import { useAuth } from '../../../../../utils/AuthContext';
import Table from '../../../admincomponents/Table';

const API_URL = 'https://tdtlworld.com/dss-kaas-backend/master/divisions/';

const DivisionManager = () => {
  const theme = useTheme();
  const { mode } = useColorMode();
  const { token } = useAuth();
  const [divisions, setDivisions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [divisionInput, setDivisionInput] = useState('');
  const [editItem, setEditItem] = useState(null);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeletingId, setIsDeletingId] = useState(null);

  const fetchDivisions = useCallback(async () => {
    if (!token) return;
    setIsPageLoading(true);
    try {
      const res = await axios.get(API_URL, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      setDivisions(res.data);
    } catch (err) {
      console.error(err);
      Swal.fire('Error', 'Failed to fetch divisions', 'error');
    } finally {
      setIsPageLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDivisions();
  }, [fetchDivisions]);

  const filteredDivisions = divisions.filter((d) =>
    d.name.toLowerCase().includes(searchTerm.toLowerCase())
  );



  const handleShowModal = () => {
    setDivisionInput('');
    setEditItem(null);
    setShowModal(true);
  };

  const handleClose = () => {
    setDivisionInput('');
    setEditItem(null);
    setShowModal(false);
  };

  const handleAddOrUpdate = async () => {
    const trimmed = divisionInput.trim();
    if (!trimmed) return;

    setIsLoading(true);
    const method = editItem ? 'PATCH' : 'POST';
    const url = editItem ? `${API_URL}${editItem.id}/` : API_URL;

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmed }),
      });

      if (!res.ok) {
        const error = await res.json();
        Swal.fire({
          title: 'Error',
          text: error.detail || `Failed to ${editItem ? 'update' : 'add'} division`,
          icon: 'error',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        });
        return;
      }

      Swal.fire({
        title: editItem ? 'Updated!' : 'Added!',
        text: `Division ${editItem ? 'updated' : 'added'} successfully.`,
        icon: 'success',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
        confirmButtonColor: theme.palette.primary.main,
      });
      fetchDivisions();
      handleClose();
    } catch (err) {
      console.error(err);
      Swal.fire({
        title: 'Error',
        text: 'Network error',
        icon: 'error',
        background: theme.palette.background.paper,
        color: theme.palette.text.primary,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (division) => {
    setDivisionInput(division.name);
    setEditItem(division);
    setShowModal(true);
  };

  const columns = [
    { 
      key: 'srNo', 
      label: 'Sr No.', 
      render: (row, index) => (
        <Typography variant="body2" color="text.secondary">
          {index + 1}
        </Typography>
      )
    },
    { 
      key: 'name', 
      label: 'Division', 
      sortable: true,
      render: (row) => (
        <Typography variant="body2" color="text.primary" fontWeight={500}>
          {row.name}
        </Typography>
      )
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      render: (row) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => handleEdit(row)}
            sx={{ color: 'primary.main' }}
          >
            <Pencil size={18} />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(row)}
            disabled={isDeletingId === row.id}
            sx={{ color: '#ff5370' }}
          >
            {isDeletingId === row.id ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <FaTrash size={16} />
            )}
          </IconButton>
        </Box>
      )
    }
  ];

  const handleDelete = async (division) => {
    const confirm = await Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${division.name}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: theme.palette.text.secondary,
      confirmButtonText: 'Yes, delete it!',
      background: theme.palette.background.paper,
      color: theme.palette.text.primary,
    });

    if (confirm.isConfirmed) {
      setIsDeletingId(division.id);

      try {
        const res = await fetch(`${API_URL}${division.id}/`, {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          const error = await res.json();
          Swal.fire({
            title: 'Error',
            text: error.detail || 'Failed to delete',
            icon: 'error',
            background: theme.palette.background.paper,
            color: theme.palette.text.primary,
          });
          return;
        }

        Swal.fire({
          title: 'Deleted!',
          text: 'Division deleted successfully.',
          icon: 'success',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          confirmButtonColor: theme.palette.primary.main,
        });
        fetchDivisions();
      } catch (err) {
        console.error(err);
        Swal.fire({
          title: 'Error',
          text: 'Network error',
          icon: 'error',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
        });
      } finally {
        setIsDeletingId(null);
      }
    }
  };

  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: '90%', sm: 400 },
    bgcolor: 'background.paper',
    borderRadius: 3,
    boxShadow: 24,
    p: 4,
    border: `1px solid ${theme.palette.divider}`
  };

  return (
    <Box sx={{ p: 3, minHeight: '100vh', bgcolor: 'background.default' }}>
      <Typography variant="h4" sx={{ mb: 4, fontWeight: 800, color: 'text.primary', textAlign: 'center' }}>
        Manage Divisions
      </Typography>

      <Card sx={{ 
        p: 3, 
        mb: 4, 
        bgcolor: 'background.paper',
        color: 'text.primary'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <TextField
            variant="outlined"
            placeholder="Search Divisions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <FaSearch color={theme.palette.text.secondary} />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: '100%', sm: 300 } }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleShowModal}
          >
            + Add Division
          </Button>
        </Box>

        {isPageLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Table
            columns={columns}
            data={filteredDivisions}
            pagination={true}
            itemsPerPage={10}
          />
        )}
      </Card>

      <Modal
        open={showModal}
        onClose={handleClose}
      >
        <Box sx={modalStyle}>
          <Typography variant="h5" sx={{ mb: 3, fontWeight: 700, color: 'text.primary' }}>
            {editItem ? 'Edit' : 'Add'} Division
          </Typography>
          
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Enter division name"
            value={divisionInput}
            onChange={(e) => setDivisionInput(e.target.value)}
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button 
              variant="outlined" 
              onClick={handleClose} 
              disabled={isLoading}
              sx={{ color: 'text.primary', borderColor: 'divider' }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleAddOrUpdate}
              disabled={isLoading}
            >
              {isLoading ? (
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              ) : null}
              {editItem ? 'Update' : 'Add'} Division
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default DivisionManager;
